import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CircularProgress from "@mui/material/CircularProgress";
import DialogDefault from "@jumbo/components/JumboDialog/components/DialogDefault";

// Output size is a prop: the caller states the exact pixel size it needs and
// every crop is produced at it. Banners are 1254x340, product photos 960x1280.
const STAGE_HEIGHT = 400;
// Longest side of the preview thumbnail, so a portrait ratio does not produce
// a preview taller than the stage next to it.
const PREVIEW_MAX = 260;
const MIN_BOX_WIDTH = 60;
const HANDLES = ["nw", "ne", "sw", "se"];

// Handles on the right edge grow the box as the pointer moves right; the other
// two grow it as the pointer moves left.
const isRightHandle = (handle) => handle === "ne" || handle === "se";
const isBottomHandle = (handle) => handle === "sw" || handle === "se";

// PNG sources keep their transparency; everything else is written as JPEG,
// which keeps a cropped image comfortably inside the 5MB upload limit.
const outputTypeFor = (file) =>
  file?.type === "image/png" ? "image/png" : "image/jpeg";

const outputNameFor = (file, mimeType) => {
  const base = (file?.name || "image").replace(/\.[^.]+$/, "");
  return `${base}${mimeType === "image/png" ? ".png" : ".jpg"}`;
};

// Transparent-checker backdrop, so letterboxed edges read as "outside the
// image" rather than as part of it.
const CHECKER = {
  backgroundColor: "#fff",
  backgroundImage:
    "linear-gradient(45deg, #d9d9d9 25%, transparent 25%), linear-gradient(-45deg, #d9d9d9 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d9d9d9 75%), linear-gradient(-45deg, transparent 75%, #d9d9d9 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
};

/**
 * The rectangle of the source image sitting under the crop box, in the image's
 * own pixels. Kept apart from the component so the maths can be verified on
 * its own.
 *
 * The box is in stage coordinates; subtracting the image's origin on the stage
 * and dividing by the fit scale converts it back to source pixels. Everything
 * is clamped into the bitmap so rounding can never ask the canvas for a pixel
 * that is not there.
 */
export const computeCropRect = ({ natural, imageRect, box }) => {
  const { scale, left, top } = imageRect;
  const sourceWidth = Math.min(natural.width, box.w / scale);
  const sourceHeight = Math.min(natural.height, box.h / scale);

  return {
    sourceWidth,
    sourceHeight,
    sourceX: Math.min(
      Math.max(0, (box.x - left) / scale),
      natural.width - sourceWidth
    ),
    sourceY: Math.min(
      Math.max(0, (box.y - top) / scale),
      natural.height - sourceHeight
    ),
  };
};

/**
 * Largest box of the required ratio that fits inside the displayed image,
 * centred - the starting selection.
 */
export const initialBox = (imageRect, aspect) => {
  const w = Math.min(imageRect.width, imageRect.height * aspect);
  const h = w / aspect;
  return {
    x: imageRect.left + (imageRect.width - w) / 2,
    y: imageRect.top + (imageRect.height - h) / 2,
    w,
    h,
  };
};

/**
 * Resize driven by one corner: the opposite corner stays pinned, the ratio is
 * locked, and the box is capped so it can never spill off the image.
 */
export const resizeBox = ({ handle, box, pointerX, imageRect, aspect }) => {
  const right = imageRect.left + imageRect.width;
  const bottom = imageRect.top + imageRect.height;

  // The pinned corner.
  const anchorX = isRightHandle(handle) ? box.x : box.x + box.w;
  const anchorY = isBottomHandle(handle) ? box.y : box.y + box.h;

  const maxByWidth = isRightHandle(handle)
    ? right - anchorX
    : anchorX - imageRect.left;
  const maxByHeight = isBottomHandle(handle)
    ? (bottom - anchorY) * aspect
    : (anchorY - imageRect.top) * aspect;

  const wanted = isRightHandle(handle) ? pointerX - anchorX : anchorX - pointerX;
  const w = Math.max(
    MIN_BOX_WIDTH,
    Math.min(wanted, maxByWidth, maxByHeight)
  );
  const h = w / aspect;

  return {
    w,
    h,
    x: isRightHandle(handle) ? anchorX : anchorX - w,
    y: isBottomHandle(handle) ? anchorY : anchorY - h,
  };
};

/** Keeps a moved box fully inside the displayed image. */
export const clampBox = (box, imageRect) => ({
  ...box,
  x: Math.min(
    Math.max(box.x, imageRect.left),
    imageRect.left + imageRect.width - box.w
  ),
  y: Math.min(
    Math.max(box.y, imageRect.top),
    imageRect.top + imageRect.height - box.h
  ),
});

/**
 * Fixed-ratio crop step for image uploads.
 *
 * The whole image is shown on the stage and a crop box is dragged/resized over
 * it. The box's ratio is locked to `width`:`height`, so a wrong-shaped image
 * cannot be produced, and whatever it covers is redrawn onto a width x height
 * canvas on save - the file handed back is always exactly that size, whatever
 * the source was.
 *
 * @param width   required output width in pixels
 * @param height  required output height in pixels
 * @param label   what is being cropped, e.g. "Banner Image"
 */
const CropDialog = ({
  open,
  file,
  index,
  total,
  width,
  height,
  label = "Image",
  onCancel,
  onCropped,
}) => {
  const aspect = width / height;
  const stageRef = useRef(null);
  const imageRef = useRef(null);
  const dragRef = useRef(null);

  const [imageUrl, setImageUrl] = useState(null);
  const [natural, setNatural] = useState(null);
  const [stageWidth, setStageWidth] = useState(0);
  const [box, setBox] = useState(null);
  const [saving, setSaving] = useState(false);

  // One object URL per picked file, released as soon as we move on.
  useEffect(() => {
    if (!file) {
      setImageUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    setNatural(null);
    setBox(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // The stage is fluid, so the crop maths works off its measured width.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      if (stageRef.current) setStageWidth(stageRef.current.clientWidth);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, imageUrl]);

  // Where the image actually sits on the stage, letterboxed to fit.
  const imageRect =
    natural && stageWidth
      ? (() => {
          const scale = Math.min(
            stageWidth / natural.width,
            STAGE_HEIGHT / natural.height
          );
          const width = natural.width * scale;
          const height = natural.height * scale;
          return {
            scale,
            width,
            height,
            left: (stageWidth - width) / 2,
            top: (STAGE_HEIGHT - height) / 2,
          };
        })()
      : null;

  // Start centred, and re-fit if the dialog is resized under us.
  useEffect(() => {
    if (imageRect) setBox(initialBox(imageRect, aspect));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [natural, stageWidth, aspect]);

  const pointerOnStage = (event) => {
    const bounds = stageRef.current.getBoundingClientRect();
    return {
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    };
  };

  const startDrag = (event, handle) => {
    if (!box || !imageRect) return;
    event.preventDefault();
    event.stopPropagation();
    // Captured on the stage, which is also where move/up are handled, so a
    // drag keeps working after the pointer leaves the box.
    stageRef.current.setPointerCapture(event.pointerId);
    dragRef.current = { handle, origin: pointerOnStage(event), box };
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || !imageRect) return;
    const point = pointerOnStage(event);

    if (drag.handle === "move") {
      setBox(
        clampBox(
          {
            ...drag.box,
            x: drag.box.x + (point.x - drag.origin.x),
            y: drag.box.y + (point.y - drag.origin.y),
          },
          imageRect
        )
      );
      return;
    }

    setBox(
      resizeBox({
        handle: drag.handle,
        box: drag.box,
        pointerX: point.x,
        imageRect,
        aspect,
      })
    );
  };

  const endDrag = (event) => {
    if (stageRef.current?.hasPointerCapture?.(event.pointerId)) {
      stageRef.current.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
  };

  const handleCrop = () => {
    const image = imageRef.current;
    if (!image || !natural || !imageRect || !box) return;

    setSaving(true);

    const { sourceX, sourceY, sourceWidth, sourceHeight } = computeCropRect({
      natural,
      imageRect,
      box,
    });

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    context.imageSmoothingQuality = "high";
    context.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      width,
      height
    );

    const mimeType = outputTypeFor(file);
    canvas.toBlob(
      (blob) => {
        setSaving(false);
        if (!blob) return;
        onCropped(
          new File([blob], outputNameFor(file, mimeType), { type: mimeType })
        );
      },
      mimeType,
      0.92
    );
  };

  // The preview is the same image scaled so the crop box fills it exactly.
  // Sized by its longest side, so a portrait crop stays a sensible thumbnail.
  const previewWidth = aspect >= 1 ? PREVIEW_MAX : PREVIEW_MAX * aspect;
  const previewScale = box ? previewWidth / box.w : 1;
  const previewStyle =
    box && imageRect
      ? {
          width: previewWidth,
          height: previewWidth / aspect,
          backgroundImage: `url(${imageUrl})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: `${imageRect.width * previewScale}px ${
            imageRect.height * previewScale
          }px`,
          backgroundPosition: `${-(box.x - imageRect.left) * previewScale}px ${
            -(box.y - imageRect.top) * previewScale
          }px`,
        }
      : null;

  const counter = total > 1 ? ` (${index + 1} of ${total})` : "";

  return (
    <DialogDefault
      open={open}
      maxWidth="lg"
      onClose={onCancel}
      title={`Crop ${label}${counter}`}
      content={
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Box
              ref={stageRef}
              onPointerMove={handlePointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              sx={{
                position: "relative",
                width: "100%",
                height: STAGE_HEIGHT,
                overflow: "hidden",
                borderRadius: 1,
                touchAction: "none",
                userSelect: "none",
                ...CHECKER,
              }}
            >
              {imageUrl && (
                <img
                  ref={imageRef}
                  src={imageUrl}
                  alt={file?.name || "banner"}
                  draggable={false}
                  onLoad={(event) =>
                    setNatural({
                      width: event.currentTarget.naturalWidth,
                      height: event.currentTarget.naturalHeight,
                    })
                  }
                  style={{
                    position: "absolute",
                    left: imageRect?.left ?? 0,
                    top: imageRect?.top ?? 0,
                    width: imageRect?.width || undefined,
                    height: imageRect?.height || undefined,
                    maxWidth: "none",
                    pointerEvents: "none",
                    visibility: imageRect ? "visible" : "hidden",
                  }}
                />
              )}

              {box && (
                <Box
                  onPointerDown={(event) => startDrag(event, "move")}
                  sx={{
                    position: "absolute",
                    left: box.x,
                    top: box.y,
                    width: box.w,
                    height: box.h,
                    cursor: "move",
                    border: "1px solid #2196f3",
                    // Dims everything outside the selection in one go.
                    boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.45)",
                  }}
                >
                  {/* Rule-of-thirds guides */}
                  {[1, 2].map((n) => (
                    <Box
                      key={`v${n}`}
                      sx={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: `${(n * 100) / 3}%`,
                        borderLeft: "1px dashed rgba(255,255,255,0.7)",
                      }}
                    />
                  ))}
                  {[1, 2].map((n) => (
                    <Box
                      key={`h${n}`}
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: `${(n * 100) / 3}%`,
                        borderTop: "1px dashed rgba(255,255,255,0.7)",
                      }}
                    />
                  ))}

                  {HANDLES.map((handle) => (
                    <Box
                      key={handle}
                      onPointerDown={(event) => startDrag(event, handle)}
                      sx={{
                        position: "absolute",
                        width: 12,
                        height: 12,
                        bgcolor: "#2196f3",
                        border: "1px solid #fff",
                        borderRadius: "2px",
                        ...(handle[0] === "n" ? { top: -6 } : { bottom: -6 }),
                        ...(handle[1] === "w" ? { left: -6 } : { right: -6 }),
                        cursor:
                          handle === "nw" || handle === "se"
                            ? "nwse-resize"
                            : "nesw-resize",
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="h5" mb={1}>
              Preview
            </Typography>
            {previewStyle ? (
              <>
                <Box
                  sx={{
                    ...previewStyle,
                    maxWidth: "100%",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                />
                <Typography variant="body2" color="text.secondary" mt={1} mb={0}>
                  Saved as {width} × {height} px
                </Typography>
                {natural && (
                  <Typography variant="body2" color="text.secondary" mb={0}>
                    Source {natural.width} × {natural.height} px
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" mb={0}>
                Loading image…
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" mt={2} mb={0}>
              Drag the box to reposition, or pull a corner to resize. The ratio
              stays locked to {width}:{height}.
            </Typography>
          </Grid>
        </Grid>
      }
      actions={
        <Stack direction="row" spacing={2}>
          <LoadingButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            variant="contained"
            size="small"
            onClick={handleCrop}
            disabled={!box}
            loading={saving}
            loadingIndicator={<CircularProgress size={20} />}
          >
            Crop &amp; Add
          </LoadingButton>
        </Stack>
      }
    />
  );
};

export default CropDialog;
